import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";

const STORAGE_KEY = "ss2026_executive_demo";
const EVENT = "ss2026:executive-demo-changed";

export function isExecutiveDemoUrl(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("demo") === "executive";
}

function readLocal(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeLocal(on: boolean) {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function useExecutiveDemoMode(): boolean {
  const [on, setOn] = useState<boolean>(() => readLocal() || isExecutiveDemoUrl());

  useEffect(() => {
    const sync = () => setOn(readLocal() || isExecutiveDemoUrl());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);

    // Auto-enable from URL
    if (isExecutiveDemoUrl() && !readLocal()) {
      writeLocal(true);
      void trackEvent("executive_demo_mode_enabled", { source: "url" });
    }

    // Hydrate from feature flag
    void supabase
      .from("feature_flags")
      .select("enabled")
      .eq("key", "executive_demo_mode_enabled")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.enabled) {
          if (!readLocal()) writeLocal(true);
        }
      });

    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return on;
}

export async function setExecutiveDemoMode(on: boolean) {
  writeLocal(on);
  await supabase
    .from("feature_flags")
    .update({ enabled: on })
    .eq("key", "executive_demo_mode_enabled");
  await supabase
    .from("feature_flags")
    .update({ enabled: on })
    .eq("key", "demo_confidential_mode_enabled");
  void trackEvent(on ? "executive_demo_mode_enabled" : "executive_demo_mode_disabled", {
    source: "admin_panel",
  });
}

export function useExecutiveDemoProtection(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const log = (kind: string) =>
      void trackEvent("demo_inspection_attempt_detected", { kind });

    const onContext = (e: MouseEvent) => {
      e.preventDefault();
      log("context_menu");
    };
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (k === "i" || k === "j" || k === "c")) ||
        (e.ctrlKey && k === "u") ||
        (e.metaKey && e.altKey && k === "i")
      ) {
        e.preventDefault();
        log(`shortcut_${k}`);
      }
    };
    const onCopy = (e: ClipboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt && tgt.closest("[data-sensitive='true']")) {
        e.preventDefault();
        log("copy_sensitive");
      }
    };
    window.addEventListener("contextmenu", onContext);
    window.addEventListener("keydown", onKey);
    document.addEventListener("copy", onCopy);
    document.body.classList.add("executive-demo-active");
    return () => {
      window.removeEventListener("contextmenu", onContext);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("copy", onCopy);
      document.body.classList.remove("executive-demo-active");
    };
  }, [enabled]);
}